import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import LandingSection from './LandingSection';
import SectionHeading from './SectionHeading';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    card: {
        boxSizing: 'border-box',
        padding: 'clamp(24px, 2.6vw, 34px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
    },
    steps: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
        gap: '26px 22px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    step: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    number: {
        display: 'grid',
        placeItems: 'center',
        width: '34px',
        height: '34px',
        marginBottom: '14px',
        borderRadius: '50%',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.tealInk,
        fontFamily: REDESIGN_FONTS.accent,
        fontSize: '1.4rem',
        fontWeight: 700,
        lineHeight: 1,
    },
    title: {
        margin: '0 0 7px',
        fontSize: '1.02rem',
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        lineHeight: 1.5,
    },
    more: {
        marginTop: '10px',
        color: REDESIGN_COLORS.link,
        fontSize: '0.84rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.primary,
        },
    },
}));

/**
 * "How it starts" as numbered steps on one card. `steps` is a list of
 * `{ key, more }`; copy comes from `${keyPrefix}.<key>.title|text`, the
 * heading from `${keyPrefix}.eyebrow|title|lead` and the link label from
 * `${keyPrefix}.more`.
 */
const StepsCard = ({ id, keyPrefix, steps }) => {
    const classes = useStyles();

    return (
        <LandingSection id={id} paddingTop={24} paddingBottom={56}>
            <SectionHeading
                eyebrow={<FormattedMessage id={`${keyPrefix}.eyebrow`} />}
                title={<FormattedMessage id={`${keyPrefix}.title`} />}
                lead={<FormattedMessage id={`${keyPrefix}.lead`} />}
                marginBottom={34}
            />

            <div className={classes.card}>
                <ol className={classes.steps}>
                    {steps.map(({ key, more }, index) => (
                        <li key={key} className={classes.step}>
                            <span aria-hidden="true" className={classes.number}>
                                {index + 1}
                            </span>
                            <h3 className={classes.title}>
                                <FormattedMessage id={`${keyPrefix}.${key}.title`} />
                            </h3>
                            <p className={classes.text}>
                                <FormattedMessage id={`${keyPrefix}.${key}.text`} />
                            </p>
                            {more ? (
                                <Link to={more} className={classes.more}>
                                    <FormattedMessage id={`${keyPrefix}.more`} />
                                </Link>
                            ) : null}
                        </li>
                    ))}
                </ol>
            </div>
        </LandingSection>
    );
};

export default StepsCard;
